import React, { useEffect, useMemo, useState } from "react";
import api from "../../services/apiClient.ts";
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { useParams } from 'react-router-dom';

// ======== إعدادات عامة: عدّل القيم حسب بيئتك ========
const API_BASE = "/v1";
const TENANT = "default-tenant-1";

const headersAuth = {
  "X-Tenant": TENANT,
  Accept: "application/json",
};

function fmtMoney(n) {
  const num = Number(n || 0);
  return `${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع`;
}

// تنسيق الرقم بفواصل الآلاف للعرض (123456789 → "123,456,789")
function formatWithCommas(val) {
  if (val === "" || val === null || val === undefined) return "";
  const str = String(val).replace(/,/g, "");
  const num = parseFloat(str);
  if (isNaN(num)) return str;
  const parts = num.toFixed(2).split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts[1] === "00" ? parts[0] : parts.join(".");
}

// استخراج الرقم من النص المنسق ("123,456,789" → 123456789)
function parseFormattedNumber(str) {
  if (!str || str === "") return "";
  const cleaned = String(str).replace(/,/g, "");
  const num = parseFloat(cleaned);
  return isNaN(num) ? "" : num;
}
function uuid() {
  if (crypto?.randomUUID) return crypto.randomUUID();
  return Date.now() + "_" + Math.random().toString(16).slice(2);
}

export default function CreateGroup() {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const { id: groupIdParam } = useParams();
  const isEditMode = Boolean(groupIdParam);
  const [show, setShow] = useState(true);
  // ====== Group Header State ======
  const [group, setGroup] = useState({
    gr_notes: "",
    plan_start_date: "",
    vault: 1,
    branch_id: 1,
  });

  let groupId = null;
  let travelersCnt = 0;

  // ====== Travelers State ======
  const [travelers, setTravelers] = useState([]);

  // ====== Agents State ======
  const [agents, setAgents] = useState([]);
  const agentsById = useMemo(() => {
    const m = new Map();
    agents.forEach((a) => m.set(a.id, a));
    return m;
  }, [agents]);

  // ====== UI State ======
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(isEditMode);
  const [isSavingExpense, setIsSavingExpense] = useState(false);

  // ====== Modal State ======
  const emptyTraveler = {
    name: "",
    phone1: "",
    phone2: "",
    passport: "",
    agent: "",
    agent_money: 0,
    paytype: "cash",
    cash: 0,
    total_price: 0,
    gr_interval: 1,
    price: 0,
    added_date: new Date().toISOString().split('T')[0],
    expenses: [],
    _initialExpenseIds: [],
    tr_id: undefined,  // لتخزين ID المسافر الموجود
  };
  const [showModal, setShowModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [tForm, setTForm] = useState(emptyTraveler);


  // ====== Payments (group expenses) ======
  const [payments, setPayments] = useState([]); // [{grp_id, amount, _grp_name}]
  const [paymentOptions, setPaymentOptions] = useState([]); // [{id,name}]

  // Modal state for payment
  const emptyPayment = { grp_id: "", amount: "", expense_date: new Date().toISOString().slice(0, 10), notes: "" };
  const [showPayModal, setShowPayModal] = useState(false);
  const [payForm, setPayForm] = useState(emptyPayment);
  const [payEditingIndex, setPayEditingIndex] = useState(-1);

  const [showQuickTypeModal, setShowQuickTypeModal] = useState(false);
  const [quickTypeName, setQuickTypeName] = useState("");
  function openQuickType() { setQuickTypeName(""); setShowQuickTypeModal(true); }
  function closeQuickType() { setShowQuickTypeModal(false); }

  // Quick Agent modal state
  const [showQuickAgentModal, setShowQuickAgentModal] = useState(false);
  const [quickAgentName, setQuickAgentName] = useState("");
  const [quickAgentPhone, setQuickAgentPhone] = useState("");
  function openQuickAgent() { setQuickAgentName(""); setQuickAgentPhone(""); setShowQuickAgentModal(true); }
  function closeQuickAgent() { setShowQuickAgentModal(false); }

  // ====== Modal تأكيد المسافر الموجود ======
  const [showExistingTravelerModal, setShowExistingTravelerModal] = useState(false);
  const [existingTravelerData, setExistingTravelerData] = useState(null);
  const [passportCheckLoading, setPassportCheckLoading] = useState(false);

  // ====== Traveler expenses modal (مصاريف عامة لكل مسافر) ======
  const [showExpensesModal, setShowExpensesModal] = useState(false);
  const [expensesTravelerIndex, setExpensesTravelerIndex] = useState(-1);
  const emptyExpense = { expense_type: "", expense_date: new Date().toISOString().split("T")[0], amount: "", notes: "" };
  const [expenseForm, setExpenseForm] = useState(emptyExpense);
  const [expenseEditingIndex, setExpenseEditingIndex] = useState(-1);


  // ====== Load Agents when branch changes ======
  useEffect(() => {
    loadAgents();
    loadGroupPaymentOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.branch_id]);

  async function loadAgents() {
    try {
      const resp = await api.get(`${API_BASE}/agents`, {
        params: { branch_id: group.branch_id, per_page: 100 },
        headers: headersAuth,
      });
      setAgents(resp.data?.data || []);
    } catch (e) {
      console.error("load agents failed", e);
    }
  }

  async function loadGroupPaymentOptions() {
    try {
      const resp = await api.get(`${API_BASE}/reports/groups-payment`, {
        params: { branch_id: group.branch_id },
        headers: headersAuth,
      });
      setPaymentOptions(resp.data?.data || []);
    } catch (e) {
      console.error("load group payment options failed", e);
    }
  }

  // ====== Load existing group + travelers when editing ======
  useEffect(() => {
    if (!isEditMode) return;

    const loadExistingGroup = async () => {
      try {
        // Load group header
        const gResp = await api.get(`${API_BASE}/groups/${groupIdParam}`, {
          headers: headersAuth,
        });
        const g = gResp.data?.data || gResp.data || {};
        setGroup((prev) => ({
          gr_notes: g.gr_notes || prev.gr_notes || "",
          plan_start_date: (g.plan_start_date && String(g.plan_start_date).slice(0, 10)) || g.created_at?.slice(0, 10) || prev.plan_start_date || "",
          vault: g.vault ?? prev.vault ?? 1,
          gr_price: g.gr_price ?? prev.gr_price ?? 0,
          branch_id: g.branch_id ?? prev.branch_id ?? 1,
        }));

        const branchForPayments = g.branch_id ?? 1;
        const optsResp = await api.get(`${API_BASE}/reports/groups-payment`, {
          params: { branch_id: branchForPayments },
          headers: headersAuth,
        });
        const opts = optsResp.data?.data || [];
        setPaymentOptions(opts);

        const bResp = await api.get(`${API_BASE}/groups/batch/${groupIdParam}`, { headers: headersAuth });
        const expenses = bResp.data?.expenses || [];
        const travelerExpensesList = bResp.data?.traveler_expenses || [];
        const batchTravelers = bResp.data?.travelers || []; // ✅ استخدم المسافرين من batch API
        const nameById = new Map(opts.map(o => [Number(o.id), o.name]));
        setPayments(expenses.map(e => ({
          id: Number(e.id),
          grp_id: Number(e.grp_id),
          amount: Number(e.amount || 0),
          expense_date: (e.expense_date && String(e.expense_date).slice(0, 10)) || "",
          notes: e.notes || "",
          travelersCnt: Number(e.travelersCnt || 0),
          _grp_name: nameById.get(Number(e.grp_id)) || "",
        })));

        // ✅ استخدم المسافرين من batch API مباشرة بدلاً من استدعاءات متعددة
        // إن لم يكن الـ group يرجع plan_start_date (مجموعات قديمة)، خذه من أول تفصيل
        if (batchTravelers.length > 0 && batchTravelers[0].plan_start_date) {
          const firstDate = typeof batchTravelers[0].plan_start_date === 'string'
            ? batchTravelers[0].plan_start_date.slice(0, 10)
            : batchTravelers[0].plan_start_date;
          setGroup((prev) => ({ ...prev, plan_start_date: firstDate || prev.plan_start_date }));
        }

        // ✅ بناء صفوف المسافرين من batch response مباشرة (بدون طلبات إضافية)
        const rows = batchTravelers.map((det) => {
          const trId = det.traveler_id || det.tr_id;
          const teList = (travelerExpensesList || []).filter(e => Number(e.tr_id) === Number(trId));
          const expensesList = teList.map(e => ({
            id: e.id,
            expense_type: e.expense_type || "",
            expense_date: (e.expense_date && String(e.expense_date).slice(0, 10)) || new Date().toISOString().split("T")[0],
            amount: Number(e.amount ?? 0),
            notes: e.notes || "",
          }));
          const _initialExpenseIds = teList.map(e => e.id).filter(Boolean);

          return {
            name: det.name || "",
            phone1: det.phone1 || "",
            phone2: det.phone2 || "",
            passport: det.passport || "",
            agent: det.agent_id || det.agent || "",
            agent_money: Number(det.agent_money || 0),
            paytype: det.paytype || 'cash',
            cash: Number(det.cash || 0),
            total_price: Number(det.total_price || 0),
            gr_interval: Number(det.gr_interval || 1),
            price: Number(det.price || 0),
            added_date: det.added_date || det.plan_start_date || new Date().toISOString().split('T')[0],
            tr_id: trId,
            gd_id: det.id,
            _agent_name: det.agent_name || "",
            expenses: expensesList,
            _initialExpenseIds,
          };
        });

        setTravelers(rows);
        const EDIT_TOAST_ID = 'group-edit-loaded-toast';
        if (!toast.isActive(EDIT_TOAST_ID)) {
          toast.info(t('groups.loadedForEdit'), { toastId: EDIT_TOAST_ID });
        }
      } catch (e) {
        console.error('Failed load existing group', e);
        const EDIT_ERR_ID = 'group-edit-load-error-toast';
        if (!toast.isActive(EDIT_ERR_ID)) {
          toast.error(t('groups.loadEditError'), { toastId: EDIT_ERR_ID });
        }
      } finally {
        setIsPageLoading(false);
      }
    };

    loadExistingGroup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, groupIdParam]);

  function openAddPayModal() {
    setPayEditingIndex(-1);
    setPayForm(emptyPayment);
    setShowPayModal(true);
  }
  function openEditPayModal(idx) {
    setPayEditingIndex(idx);
    setPayForm({
      grp_id: payments[idx].grp_id,
      amount: payments[idx].amount,
      expense_date: (payments[idx].expense_date && String(payments[idx].expense_date).slice(0, 10)) || new Date().toISOString().slice(0, 10),
      notes: payments[idx].notes || "",
    });
    setShowPayModal(true);
  }
  function closePayModal() { setShowPayModal(false); }

  const [isSavingPayment, setIsSavingPayment] = useState(false);
  function savePaymentFromModal() {
    // حماية ضد الضغط المتكرر
    if (isSavingPayment) return;

    if (!payForm.grp_id) {
      toast.error(t('payments.validation.expenseTypeRequired'));
      return;
    }
    if (Number(payForm.amount) <= 0) {
      toast.error(t('payments.validation.amountInvalid'));
      return;
    }

    setIsSavingPayment(true);

    const opt = paymentOptions.find(o => String(o.id) === String(payForm.grp_id));
    const existing = payEditingIndex >= 0 ? payments[payEditingIndex] : null;
    const row = {
      id: existing?.id,
      grp_id: Number(payForm.grp_id),
      amount: Number(payForm.amount),
      expense_date: payForm.expense_date || new Date().toISOString().slice(0, 10),
      notes: (payForm.notes || "").trim(),
      _grp_name: opt ? opt.name : "",
    };
    setPayments(prev => {
      const clone = [...prev];
      if (payEditingIndex >= 0) clone[payEditingIndex] = row;
      else clone.push(row);
      return clone;
    });
    closePayModal();

    // إعادة تعيين الحماية بعد إغلاق المودال
    setTimeout(() => setIsSavingPayment(false), 300);
  }
  function removePayment(idx) {
    if (!window.confirm("حذف هذا القيد؟")) return;
    const p = payments[idx];
    const doRemoveLocal = () => setPayments(prev => prev.filter((_, i) => i !== idx));
    if (isEditMode && p?.id) {
      api.delete(`${API_BASE}/groups-money/${p.id}`, { headers: headersAuth })
        .then(() => doRemoveLocal())
        .catch(() => doRemoveLocal());
    } else {
      doRemoveLocal();
    }
  }


  // ====== Traveler Modal Handlers ======
  function openAddModal() {
    setEditingIndex(-1);
    setTForm(() => {
      // In create mode, default traveler total to group price for quicker entry
      if (!isEditMode) {
        return { ...emptyTraveler, total_price: Number(group.gr_price || 0) };
      }
      return emptyTraveler;
    });
    setShowModal(true);
  }
  function openEditModal(idx) {
    setEditingIndex(idx);
    setTForm({
      ...emptyTraveler,
      ...travelers[idx],
      agent: travelers[idx].agent || "",
    });
    setShowModal(true);
  }
  function closeModal() {
    setShowModal(false);
  }

  function openExpensesModal(idx) {
    setExpensesTravelerIndex(idx);
    setExpenseForm(emptyExpense);
    setExpenseEditingIndex(-1);
    setShowExpensesModal(true);
  }
  function closeExpensesModal() {
    setShowExpensesModal(false);
    setExpensesTravelerIndex(-1);
  }
  function addExpenseRow() {
    setExpenseForm(emptyExpense);
    setExpenseEditingIndex(-1);
  }
  function editExpenseRow(idx) {
    const t = travelers[expensesTravelerIndex];
    const list = t?.expenses || [];
    const e = list[idx];
    if (e) {
      setExpenseForm({
        expense_type: e.expense_type || "",
        expense_date: (e.expense_date && String(e.expense_date).slice(0, 10)) || new Date().toISOString().split("T")[0],
        amount: e.amount !== undefined && e.amount !== null ? e.amount : "",
        notes: e.notes || "",
      });
      setExpenseEditingIndex(idx);
    }
  }
  function saveExpenseFromModal() {
    if (isSavingExpense) return;
    if (!expenseForm.expense_type?.trim()) {
      toast.error('نوع المصروف مطلوب');
      return;
    }
    if (!expenseForm.expense_date) {
      toast.error('التاريخ مطلوب');
      return;
    }
    const amt = Number(expenseForm.amount);
    if (isNaN(amt) || amt < 0) {
      toast.error('المبلغ مطلوب ويجب أن يكون رقماً صحيحاً');
      return;
    }
    setIsSavingExpense(true);
    const idx = expensesTravelerIndex;
    if (idx < 0 || idx >= travelers.length) { setIsSavingExpense(false); return; }
    const row = {
      expense_type: expenseForm.expense_type.trim(),
      expense_date: expenseForm.expense_date,
      amount: amt,
      notes: (expenseForm.notes || "").trim(),
    };
    if (expenseEditingIndex >= 0) {
      const current = travelers[idx].expenses?.[expenseEditingIndex];
      if (current?.id) row.id = current.id;
    }
    setTravelers(prev => {
      const next = [...prev];
      const list = [...(next[idx].expenses || [])];
      if (expenseEditingIndex >= 0) {
        list[expenseEditingIndex] = row;
      } else {
        list.push(row);
      }
      next[idx] = { ...next[idx], expenses: list };
      return next;
    });
    setExpenseForm(emptyExpense);
    setExpenseEditingIndex(-1);
    setTimeout(() => setIsSavingExpense(false), 300);
  }
  function removeExpenseRow(expenseIdx) {
    if (!window.confirm(t('common.deleteConfirmation') || 'حذف هذا المصروف؟')) return;
    setTravelers(prev => {
      const next = [...prev];
      const list = (next[expensesTravelerIndex].expenses || []).filter((_, i) => i !== expenseIdx);
      next[expensesTravelerIndex] = { ...next[expensesTravelerIndex], expenses: list };
      return next;
    });
    setExpenseForm(emptyExpense);
    setExpenseEditingIndex(-1);
  }

  // تحقق من المسافر بجواز السفر
  async function checkTravelerByPassport(passport, grId = null) {
    if (!passport || !passport.trim()) return null;
    try {
      const resp = await api.post(
        `${API_BASE}/travelers/check`,
        { passport: passport.trim(), gr_id: grId },
        { headers: headersAuth }
      );
      return resp.data;
    } catch {
      return null;
    }
  }

  // التحقق عند مغادرة حقل الجواز
  async function handlePassportBlur(e) {
    const passport = e.target.value.trim();
    if (!passport) return;

    // تجاهل إذا كنا في وضع التعديل ولم يتغير الجواز
    if (editingIndex >= 0) {
      const current = travelers[editingIndex];
      if (current && current.passport === passport) return;
    }

    // التحقق من أن هذا الجواز غير موجود في المسافرين الحاليين بهذه المجموعة
    const alreadyInGroup = travelers.some((t, idx) =>
      idx !== editingIndex && t.passport === passport
    );

    if (alreadyInGroup) {
      toast.error(t('travelers.alreadyInGroup') || 'هذا المسافر موجود بالفعل في هذه المجموعة', {
        position: "top-right",
        autoClose: 3000,
        style: { backgroundColor: '#ef4444', color: 'white' }
      });
      return;
    }

    setPassportCheckLoading(true);
    const result = await checkTravelerByPassport(passport, groupId);
    setPassportCheckLoading(false);

    if (result?.exists) {
      if (result.in_same_group) {
        // المسافر موجود في نفس المجموعة (من الـ database)
        toast.error(t('travelers.alreadyInGroup') || 'هذا المسافر موجود بالفعل في هذه المجموعة', {
          position: "top-right",
          autoClose: 3000,
          style: { backgroundColor: '#ef4444', color: 'white' }
        });
      } else {
        // المسافر موجود في النظام - اسأل المستخدم
        setExistingTravelerData(result.traveler);
        setShowExistingTravelerModal(true);
      }
    }
  }

  // تأكيد استخدام المسافر الموجود
  function confirmUseExistingTraveler() {
    if (existingTravelerData) {
      setTForm(prev => ({
        ...prev,
        name: existingTravelerData.name || '',
        phone1: existingTravelerData.phone1 || '',
        phone2: existingTravelerData.phone2 || '',
        passport: existingTravelerData.passport || '',
        tr_id: existingTravelerData.id,
      }));
    }
    setShowExistingTravelerModal(false);
    setExistingTravelerData(null);
  }

  // إلغاء استخدام المسافر الموجود
  function cancelUseExistingTraveler() {
    setShowExistingTravelerModal(false);
    setExistingTravelerData(null);
    // مسح حقل الجواز
    setTForm(prev => ({ ...prev, passport: '' }));
  }

  async function saveTravelerFromModal() {
    // Validation بسيط
    if (!tForm.name.trim()) {
      toast.error(t('travelers.validationRequired'), {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          backgroundColor: '#ef4444',
          color: 'white'
        }
      });
      return;
    }
    if (tForm.paytype === "cash" && Number(tForm.cash) <= 0) {
      toast.error(t('travelers.validationAmountInvalid'), {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          backgroundColor: '#ef4444',
          color: 'white'
        }
      });
      return;
    }
    if (tForm.paytype === "deferred" && Number(tForm.total_price) <= 0) {
      toast.error(t('travelers.validationAmountInvalid'), {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: { backgroundColor: '#ef4444', color: 'white' }
      });
      return;
    }
    if (tForm.paytype === "installments" && Number(tForm.price) <= 0) {
      toast.error(t('travelers.validationAmountInvalid'), {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          backgroundColor: '#ef4444',
          color: 'white'
        }
      });
      return;
    }
    if (tForm.paytype === "installments" && Number(tForm.gr_interval || 0) < 1) {
      toast.error(t('travelers.validationAmountInvalid'), {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          backgroundColor: '#ef4444',
          color: 'white'
        }
      });
      return;
    }
    // التحقق من إجمالي السعر - يُستثنى المجاني لأنه لا يدفع أي مبلغ
    if (tForm.paytype !== "free" && Number(tForm.total_price) <= 0) {
      toast.error(t('travelers.validationAmountInvalid'), {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          backgroundColor: '#ef4444',
          color: 'white'
        }
      });
      return;
    }

    // التحقق من عدم تكرار المسافر في نفس المجموعة (بالجواز)
    const passport = tForm.passport.trim();
    if (passport) {
      const alreadyInGroup = travelers.some((t, idx) =>
        idx !== editingIndex && t.passport === passport
      );
      if (alreadyInGroup) {
        toast.error(t('travelers.alreadyInGroup') || 'هذا المسافر موجود بالفعل في هذه المجموعة', {
          position: "top-right",
          autoClose: 3000,
          style: { backgroundColor: '#ef4444', color: 'white' }
        });
        return;
      }
    }

    // استخدام tr_id من النموذج إذا تم جلبه من مسافر موجود
    const trId = tForm.tr_id;

    const current = editingIndex >= 0 ? travelers[editingIndex] : null;
    const isFree = tForm.paytype === "free";
    const isDeferred = tForm.paytype === "deferred";
    const row = {
      name: tForm.name.trim(),
      phone1: tForm.phone1.trim(),
      phone2: tForm.phone2.trim(),
      passport: tForm.passport.trim(),
      agent: tForm.agent ? Number(tForm.agent) : null,
      agent_money: Number(tForm.agent_money || 0),
      paytype: tForm.paytype,
      cash: (isFree || isDeferred) ? 0 : Number(tForm.cash || 0),
      total_price: isFree ? 0 : Number(tForm.total_price || 0),
      gr_interval: (isFree || isDeferred) ? 0 : Number(tForm.gr_interval || 1),
      price: (isFree || isDeferred) ? 0 : Number(tForm.price || 0),
      added_date: tForm.added_date || new Date().toISOString().split('T')[0],
      tr_id: (current && current.tr_id) ? current.tr_id : (trId || undefined),
      gd_id: current && current.gd_id ? current.gd_id : undefined,
      _agent_name:
        tForm.agent && agentsById.get(Number(tForm.agent))
          ? `${agentsById.get(Number(tForm.agent)).name}${agentsById.get(Number(tForm.agent)).phone
            ? " (" + agentsById.get(Number(tForm.agent)).phone + ")"
            : ""
          }`
          : "",
      expenses: (current && Array.isArray(current.expenses)) ? current.expenses : [],
      _initialExpenseIds: (current && Array.isArray(current._initialExpenseIds)) ? current._initialExpenseIds : [],
    };

    // Persist to backend when editing an existing traveler in edit mode
    const isEditingExisting = isEditMode && editingIndex >= 0 && current && current.tr_id;
    if (isEditingExisting) {
      const travelerId = current.tr_id;
      try {
        // Update traveler core fields using modal inputs
        await api.put(
          `${API_BASE}/travelers/${travelerId}`,
          {
            name: row.name,
            phone1: row.phone1,
            phone2: row.phone2,
            passport: row.passport,
            branch_id: Number(group.branch_id || 1),
          },
          { headers: headersAuth }
        );

        // Update group-detail fields if available
        if (current.gd_id) {
          const detailPayload = {
            gr_id: Number(groupIdParam),
            tr_id: Number(travelerId),
            paytype: row.paytype,
            total_price: Number(row.total_price),
            plan_start_date: group.plan_start_date,
            added_date: row.added_date || new Date().toISOString().split('T')[0],
            branch_id: Number(group.branch_id || 1),
            vault: Number(group.vault || 1),
            agent_money: Number(row.agent_money || 0),
            // Explicitly reset fields depending on paytype to reflect dropdown changes
            gr_interval: row.paytype === 'installments' ? Number(row.gr_interval || 1) : 0,
            price: row.paytype === 'installments' ? Number(row.price || 0) : 0,
            cash: (row.paytype === 'installments' || row.paytype === 'free' || row.paytype === 'deferred') ? 0 : Number(row.cash || 0),
          };
          const selectedAgent = row.agent ? agentsById.get(Number(row.agent)) : null;
          if (selectedAgent) {
            detailPayload.agent = { name: selectedAgent.name, phone: selectedAgent.phone || "" };
          }
          await api.put(`${API_BASE}/group-details/${current.gd_id}`, detailPayload, { headers: headersAuth });
        }

        toast.success(t('common.success'));
      } catch (e) {
        const baseMsg = e?.response?.data?.message || e.message;
        toast.error(baseMsg || t('common.error'));
        return; // stop if backend update failed
      }
    }

    setTravelers((prev) => {
      const clone = [...prev];
      if (editingIndex >= 0) clone[editingIndex] = row;
      else clone.push(row);
      return clone;
    });

    closeModal();
  }

  async function removeTraveler(idx) {
    if (!window.confirm(t('travelers.deleteConfirmation'))) return;
    const row = travelers[idx];
    try {
      if (isEditMode && row?.gd_id) {
        await api.delete(`${API_BASE}/group-details/${row.gd_id}`, { headers: headersAuth });
        // حذف مصاريف هذا المسافر في هذه المجموعة
        const expenseIds = (row.expenses || []).filter(e => e.id).map(e => e.id);
        for (const id of expenseIds) {
          try {
            await api.delete(`${API_BASE}/traveler-expenses/${id}`, { headers: headersAuth });
          } catch (_) { /* ignore per-item errors */ }
        }
        toast.success(t('common.success'));
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || t('common.error');
      toast.error(msg);
      return;
    }
    setTravelers((prev) => prev.filter((_, i) => i !== idx));
  }

  async function quickCreateAgent() {
    openQuickAgent();
  }

  // ====== Submit Group ======
  async function submitGroup() {
    // حماية ضد التكرار: إذا كان الإرسال جارياً، لا تستمر
    if (isSubmitting) {
      console.log('submitGroup already in progress, skipping...');
      return;
    }

    if (!group.gr_notes.trim()) {
      toast.error(t('groups.groupNameRequired'), {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          backgroundColor: '#ef4444',
          color: 'white'
        }
      });
      return;
    }
    if (!group.plan_start_date) {
      toast.error(t('groups.groupDateRequired'), {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          backgroundColor: '#ef4444',
          color: 'white'
        }
      });
      return;
    }
    /*
    if (travelers.length === 0) {
      toast.error(t('groups.atLeastOneUserRequired'), {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          backgroundColor: '#ef4444',
          color: 'white'
        }
      });
      return;
    }
    if (!isEditMode && payments.length === 0) {
      toast.error('يجب إضافة مصاريف المجموعة قبل الحفظ', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          backgroundColor: '#ef4444',
          color: 'white'
        }
      });
      return;
    }*/

    setIsSubmitting(true);

    try {
      // معرّفات المصاريف الجديدة التي يُرجعها الخادم بعد الحفظ (لمنع التكرار)
      let newExpenseMap = [];

      // Branch: create vs edit

      if (!isEditMode) {
        // إنشاء مجموعة جديدة
        const groupResp = await api.post(
          `${API_BASE}/groups`,
          {
            gr_notes: group.gr_notes.trim(),
            plan_start_date: group.plan_start_date,
            gr_in: 0,
            gr_out: 0,
            gr_price: Number(group.gr_price || 0),
            vault: Number(group.vault || 1),
            branch_id: Number(group.branch_id || 1),
          },
          { headers: headersAuth }
        );
        groupId = groupResp?.data?.data?.id;
        if (!groupId) throw new Error("Group ID missing from API response");

        // إنشاء/تحديث المسافرين وربطهم بالمجموعة عبر group-details (للمجموعات الجديدة)
        for (const t of travelers) {
          let travelerId = t.tr_id;
          travelersCnt = travelersCnt + 1;
          if (travelerId) {
            await api.put(
              `${API_BASE}/travelers/${travelerId}`,
              {
                name: t.name,
                phone1: t.phone1,
                phone2: t.phone2,
                passport: t.passport,
                branch_id: Number(group.branch_id || 1),
              },
              { headers: headersAuth }
            );
          } else {
            const travelerResp = await api.post(
              `${API_BASE}/travelers`,
              {
                name: t.name,
                phone1: t.phone1,
                phone2: t.phone2,
                passport: t.passport,
                branch_id: Number(group.branch_id || 1),
              },
              { headers: headersAuth }
            );
            travelerId = travelerResp?.data?.data?.id;
            if (!travelerId) throw new Error("Traveler ID missing from API response");
          }

          const isInstallments = t.paytype === "installments";
          const isFree = t.paytype === "free";
          const isDeferred = t.paytype === "deferred";
          const travelerExpensesTotal = (t.expenses || []).reduce((s, e) => s + Number(e.amount ?? 0), 0);
          const detailPayload = {
            gr_id: Number(groupId),
            tr_id: Number(travelerId),
            paytype: t.paytype,
            total_price: isFree ? 0 : Number(t.total_price),
            cash: (isInstallments || isFree || isDeferred) ? 0 : Number(t.cash || 0),
            price: isInstallments ? Number(t.price || 0) : 0,
            plan_start_date: group.plan_start_date,
            added_date: t.added_date || new Date().toISOString().split('T')[0],
            branch_id: Number(group.branch_id || 1),
            vault: Number(group.vault || 1),
            agent_money: Number(t.agent_money || 0),
            gr_interval: isInstallments ? Number(t.gr_interval || 1) : (isDeferred ? 0 : 1),
            traveler_expenses_total: travelerExpensesTotal,
          };

          if (t.agent) {
            const selectedAgent = agentsById.get(Number(t.agent));
            if (selectedAgent) {
              detailPayload.agent = { name: selectedAgent.name, phone: selectedAgent.phone || "" };
            }
          }

          if (t.gd_id) {
            await api.put(`${API_BASE}/group-details/${t.gd_id}`, detailPayload, { headers: headersAuth });
          } else {
            await api.post(`${API_BASE}/group-details`, detailPayload, { headers: headersAuth });
          }

          // مصاريف المسافر العامة
          const expenses = t.expenses || [];
          const branchId = Number(group.branch_id || 1);
          const headersWithBranch = { ...headersAuth, "X-Branch": String(branchId) };
          for (let expIdx = 0; expIdx < expenses.length; expIdx++) {
            const exp = expenses[expIdx];
            const payload = {
              gr_id: Number(groupId),
              tr_id: Number(travelerId),
              branch_id: branchId,
              expense_type: (exp.expense_type || "").trim(),
              expense_date: exp.expense_date,
              amount: Number(exp.amount ?? 0),
              notes: (exp.notes || "").trim() || null,
            };
            if (payload.expense_type && payload.expense_date) {
              await api.post(`${API_BASE}/traveler-expenses`, payload, { headers: headersWithBranch });
            }
          }
        }

        // مصاريف المجموعة للمجموعات الجديدة
        if (payments.length > 0) {
          console.log('📦 Sending payments batch:', payments.length, 'items', payments);
          const items = payments.map(p => ({
            gr_id: Number(groupId),
            grp_id: p.grp_id,
            travelersCnt: travelersCnt,
            amount: Number(p.amount),
            expense_date: p.expense_date || null,
            notes: (p.notes || "").trim() || null,
          }));
          console.log('📦 Batch items to send:', items);
          await api.post(`${API_BASE}/groups-money/batch`, { items }, { headers: { ...headersAuth, "X-Branch": String(group.branch_id || 1) } });
        }
      } else {
        // ===== وضع التعديل: استخدام Batch Update API لتحسين الأداء =====
        groupId = groupIdParam;
        travelersCnt = travelers.length;

        // تحضير بيانات المسافرين للإرسال دفعة واحدة
        const travelersPayload = travelers.map(t => {
          const isInstallments = t.paytype === "installments";
          const isFree = t.paytype === "free";
          const isDeferred = t.paytype === "deferred";
          const travelerExpensesTotal = (t.expenses || []).reduce((s, e) => s + Number(e.amount ?? 0), 0);

          return {
            tr_id: t.tr_id || null,
            gd_id: t.gd_id || null,
            name: String(t.name || "").trim(),
            phone1: String(t.phone1 || "").trim(),
            phone2: t.phone2 || null,
            passport: String(t.passport || "").trim(),
            agent: t.agent ? Number(t.agent) : null,
            agent_money: Number(t.agent_money || 0),
            paytype: t.paytype,
            total_price: isFree ? 0 : Number(t.total_price || 0),
            cash: (isInstallments || isFree || isDeferred) ? 0 : Number(t.cash || 0),
            price: isInstallments ? Number(t.price || 0) : 0,
            gr_interval: isInstallments ? Number(t.gr_interval || 1) : (isDeferred ? 0 : 1),
            traveler_expenses_total: travelerExpensesTotal,
            expenses: (t.expenses || []).map(exp => ({
              id: exp.id ? Number(exp.id) : null,
              expense_type: (exp.expense_type || "").trim(),
              expense_date: exp.expense_date || null,
              amount: Number(exp.amount ?? 0),
              notes: (exp.notes || "").trim() || null,
            })),
            _initialExpenseIds: (t._initialExpenseIds || []).map(id => Number(id)),
          };
        });

        // إرسال طلب batch update واحد بدلاً من 75+ طلب
        const batchResp = await api.put(
          `${API_BASE}/groups/batch/${groupIdParam}`,
          {
            group: {
              gr_notes: group.gr_notes.trim(),
              plan_start_date: group.plan_start_date,
              vault: Number(group.vault || 1),
              gr_price: Number(group.gr_price || 0),
              branch_id: Number(group.branch_id || 1),
            },
            travelers: travelersPayload,
          },
          { headers: headersAuth }
        );
        // استخراج معرّفات المصاريف الجديدة من الاستجابة لتحديث الحالة المحلية
        newExpenseMap = batchResp?.data?.traveler_expense_ids || [];

        // مصاريف المجموعة (تبقى كما هي لأنها منفصلة)
        const existingPayments = payments.filter(p => p.id);
        const newPayments = payments.filter(p => !p.id);

        // تحديث المصاريف الموجودة + إضافة الجديدة بالتوازي
        const paymentPromises = [];
        for (const p of existingPayments) {
          paymentPromises.push(
            api.put(`${API_BASE}/groups-money/${p.id}`,
              { amount: Number(p.amount), grp_id: Number(p.grp_id), expense_date: p.expense_date || null, notes: (p.notes || "").trim() || null },
              { headers: { ...headersAuth, "X-Branch": String(group.branch_id || 1) } }
            )
          );
        }
        if (newPayments.length > 0) {
          const items = newPayments.map(p => ({
            gr_id: Number(groupId),
            grp_id: Number(p.grp_id),
            travelersCnt: travelersCnt,
            amount: Number(p.amount),
            expense_date: p.expense_date || null,
            notes: (p.notes || "").trim() || null,
          }));
          paymentPromises.push(
            api.post(`${API_BASE}/groups-money/batch`, { items }, { headers: { ...headersAuth, "X-Branch": String(group.branch_id || 1) } })
          );
        }
        if (paymentPromises.length > 0) {
          await Promise.all(paymentPromises);
        }
      }

      toast.success(
        isEditMode ? (t('groups.groupUpdatedSuccessfully') || 'تم تحديث المجموعة بنجاح') : t('groups.groupCreatedSuccessfully'),
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          style: { backgroundColor: '#10b981', color: 'white' }
        }
      );
      if (!isEditMode) {
        setTravelers([]);
        setPayments([]);
        setGroup({ gr_notes: "", plan_start_date: "", vault: 1, gr_price: 0, branch_id: group.branch_id });
      } else {
        // في وضع التعديل: تحديث الـ state بالـ IDs الجديدة لمنع التكرار عند الحفظ مرة أخرى
        setTravelers(prev => prev.map((traveler, tIdx) => {
          const updatedExpenses = (traveler.expenses || []).map((exp, eIdx) => {
            const { _justAdded, ...rest } = exp; // إزالة العلامة المؤقتة
            // تطبيق المعرّف الجديد من الخادم إذا كان هذا مصروفاً أُضيف حديثاً
            if (!rest.id) {
              const match = newExpenseMap.find(
                m => m.traveler_index === tIdx && m.expense_index === eIdx
              );
              if (match) {
                rest.id = match.id;
              }
            }
            return rest;
          });
          return {
            ...traveler,
            expenses: updatedExpenses,
            _initialExpenseIds: updatedExpenses.filter(e => e.id).map(e => String(e.id)),
          };
        }));
      }
    } catch (e) {
      console.error(e);
      const baseMsg = e.response?.data?.message || e.message;
      const errs = e.response?.data?.errors;
      const details = errs && typeof errs === 'object'
        ? ' — ' + Object.entries(errs).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' | ')
        : '';
      toast.error(`${t('groups.saveFailed')}: ${baseMsg}${details}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          backgroundColor: '#ef4444',
          color: 'white'
        }
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isPageLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-lg font-medium text-gray-600 dark:text-gray-300">{t('common.loading') || 'جاري التحميل...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="text-gray-900 dark:text-gray-100 py-6 px-3 sm:px-4 lg:px-6" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="w-full">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-100 dark:border-gray-700 mb-5 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-3">
            <div className="flex items-center space-x-reverse space-x-2.5">
              <div className="bg-white/20 rounded-lg p-1.5">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <h2 className="text-base font-semibold text-white leading-tight">{t('groups.createNewGroup')}</h2>
                <p className="text-blue-100 text-xs mt-0.5">{t('groups.createGroupSubtitle')}</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="lg:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('groups.groupName')}
                </label>
                <div className="relative group">
                  <input
                    type="text"
                    className="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-lg group-hover:border-emerald-300 group-hover:shadow-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-300"
                    placeholder={t('groups.groupNamePlaceholder')}
                    value={group.gr_notes}
                    onChange={(e) => setGroup((g) => ({ ...g, gr_notes: e.target.value }))}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-emerald-500 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('groups.planStartDate')}
                </label>
                <div className="relative group">
                  <input
                    type="date"
                    className="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 group-hover:border-emerald-300 group-hover:shadow-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    value={group.plan_start_date}
                    onChange={(e) => setGroup((g) => ({ ...g, plan_start_date: e.target.value }))}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-emerald-500 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
              </div>

              {!show &&
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 text-center">
                    {t('groups.vault')}
                  </label>
                  <div className="relative group">
                    <select
                      className="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 group-hover:border-emerald-300 group-hover:shadow-md appearance-none bg-white dark:bg-gray-700 text-center dark:text-gray-100"
                      value={group.vault}
                      onChange={(e) => setGroup((g) => ({ ...g, vault: e.target.value }))}
                    >
                      <option value="1">{t('groups.mainVault')}</option>
                      <option value="2">{t('groups.subVault')}</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400 group-hover:text-emerald-500 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                  </div>
                </div>
              }
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 text-center">
                  {t('groups.SeatPrice')}
                </label>
                <div className="relative group">
                  <input
                    type="text"
                    inputMode="numeric"
                    className="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-lg group-hover:border-emerald-300 group-hover:shadow-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-300"
                    placeholder={t('groups.groupPrice')}
                    value={formatWithCommas(group.gr_price)}
                    onChange={(e) => {
                      const parsed = parseFormattedNumber(e.target.value);
                      setGroup((g) => ({ ...g, gr_price: parsed === "" ? "" : parsed }));
                    }}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-emerald-500 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            {!show &&
              <div className="mt-6 max-w-xs ml-auto">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 text-center">
                  {t('groups.branch')}
                </label>
                <div className="relative group">
                  <select
                    className="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 group-hover:border-emerald-300 group-hover:shadow-md appearance-none bg-white dark:bg-gray-700 text-center dark:text-gray-100"
                    value={group.branch_id}
                    onChange={(e) => setGroup((g) => ({ ...g, branch_id: e.target.value }))}
                  >
                    <option value="1">{t('groups.mainBranch')}</option>
                    <option value="2">{t('groups.otherBranch')}</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-emerald-500 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                </div>
              </div>
            }
          </div>
        </div>


        {/* Group Expenses Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-100 dark:border-gray-700 overflow-hidden mb-5">
          <div className="bg-gradient-to-r from-purple-600 to-fuchsia-600 px-5 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-reverse space-x-2.5">
                <div className="bg-white/20 rounded-lg p-1.5">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .843-3 1.882V12h6v-2.118C15 8.843 13.657 8 12 8zM6 12v4a6 6 0 0012 0v-4M8 21h8" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-base font-semibold text-white leading-tight">{t('groups.expensesTitle')}</h2>
                  <p className="text-pink-100 text-xs mt-0.5">{t('groups.expensesSubtitle')}</p>
                </div>
              </div>
              <button
                className="bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200"
                onClick={openAddPayModal}
              >
                + {t('groups.addExpense')}
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="app-table-wrap">
            <table className="app-table min-w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 border-b border-gray-200 dark:border-gray-600">
                <tr>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">{t('groups.expenseType')}</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">{t('common.amount')}</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">{t('common.date') || 'التاريخ'}</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">{t('common.notes') || 'ملاحظات'}</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {payments.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-10 text-center text-gray-500">{t('groups.noExpenses')}</td>
                  </tr>
                ) : (
                  payments.map((p, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">{p._grp_name}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">{fmtMoney(p.amount)}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">{p.expense_date || '—'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-[200px] truncate" title={p.notes || ''}>{p.notes || '—'}</td>
                      <td className="px-6 py-4 text-sm font-medium">
                        <div className="flex space-x-reverse space-x-2">
                          <button className="text-indigo-600 hover:text-indigo-900" onClick={() => openEditPayModal(idx)}>{t('common.edit')}</button>
                          <button className="text-red-600 hover:text-red-900" onClick={() => removePayment(idx)}>{t('common.delete')}</button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>




        {/* Travelers Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-5 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-reverse space-x-2.5">
                <div className="bg-white/20 rounded-lg p-1.5">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-base font-semibold text-white leading-tight">{t('groups.travelersTitle')}</h2>
                  <p className="text-emerald-100 text-xs mt-0.5">{t('groups.manageTravelersHelp')}</p>
                </div>
              </div>
              <button
                className="bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-reverse space-x-1.5 backdrop-blur-sm"
                onClick={openAddModal}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span>{t('travelers.addTraveler')}</span>
              </button>
            </div>
          </div>

          {/* Travelers Table View (responsive) */}
          <div className="app-table-wrap shadow-sm">
            <table className="app-table w-full" style={{ tableLayout: 'auto' }}>
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 border-b border-gray-200 dark:border-gray-600">
                <tr>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap w-10">#</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">{t('travelers.fullName')}</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">{t('travelers.primaryPhone')}</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">{t('travelers.agentName')}</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">{t('travelers.agentCommission')}</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">{t('travelers.paymentType')}</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">{t('travelers.cashReceived')}</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">{t('travelers.totalPrice')}</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">{t('travelers.paymentDuration')}</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">{t('travelers.monthlyInstallment')}</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">إجمالي الصرفيات</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">{t('travelers.passportNumber')}</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {travelers.length === 0 ? (
                  <tr>
                    <td colSpan={13} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center space-y-3">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <p className="text-gray-500 font-medium">{t('travelers.noTravelers')}</p>
                        <p className="text-gray-400 text-sm">{t('groups.addTravelerCTA')}</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  travelers.map((traveler, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150">
                      <td className="px-3 py-3 text-center text-sm font-bold text-gray-700 dark:text-gray-300 whitespace-nowrap w-10">
                        {idx + 1}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="flex items-center space-x-reverse space-x-3">
                          <div className="w-9 h-9 shrink-0 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                            {traveler.name.charAt(0)}
                          </div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{traveler.name}</div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">{traveler.phone1}</td>
                      <td className="px-3 py-3 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
                        {traveler._agent_name || (traveler.agent ? agentsById.get(traveler.agent)?.name : "") || "—"}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtMoney(traveler.agent_money)}</td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${traveler.paytype === "free"
                          ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                          : traveler.paytype === "installments"
                            ? "bg-blue-100 text-blue-800"
                            : traveler.paytype === "deferred"
                              ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"
                              : "bg-green-100 text-green-800"
                          }`}>
                          {traveler.paytype === "free"
                            ? t('travelers.free')
                            : traveler.paytype === "installments"
                              ? t('travelers.installments')
                              : traveler.paytype === "deferred"
                                ? t('travelers.deferred')
                                : t('travelers.cash')}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtMoney(traveler.cash)}</td>
                      <td className="px-3 py-3 text-sm font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtMoney(traveler.total_price)}</td>
                      <td className="px-3 py-3 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">{traveler.gr_interval || 1}</td>
                      <td className="px-3 py-3 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtMoney(traveler.price)}</td>
                      <td className="px-3 py-3 text-sm font-medium text-amber-700 dark:text-amber-400 whitespace-nowrap">
                        {fmtMoney((traveler.expenses || []).reduce((s, e) => s + Number(e.amount ?? 0), 0))}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">{traveler.passport}</td>
                      <td className="px-6 py-3 text-sm font-medium">
                        <div className="flex items-center gap-2 px-3 flex-nowrap">
                          <button
                            type="button"
                            className="px-3 py-1 rounded-md bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-800/50 transition-colors shrink-0"
                            onClick={() => openExpensesModal(idx)}
                            title="الصرفيات"
                          >
                            الصرفيات
                            {(traveler.expenses?.length || 0) > 0 && (
                              <span className="mr-1 rtl:ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 text-xs font-bold rounded-full bg-amber-500 text-white">
                                {traveler.expenses.length}
                              </span>
                            )}
                          </button>
                          <button
                            className="px-3 text-indigo-600 hover:text-indigo-900 transition-colors duration-150 shrink-0"
                            onClick={() => openEditModal(idx)}
                          >
                            تعديل
                          </button>
                          <button
                            className="text-red-600 hover:text-red-900 pl-6 transition-colors duration-150 shrink-0"
                            onClick={() => removeTraveler(idx)}
                          >
                            حذف
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Removed mobile cards; unified table view across breakpoints */}
        </div>


        {/* Payment Modal */}
        {showPayModal && (
          <div
            className={`fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 ${showQuickTypeModal ? 'pointer-events-none aria-hidden' : ''
              }`}
            aria-hidden={showQuickTypeModal ? 'true' : 'false'}
          >
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full pointer-events-auto">
              <div className="bg-gradient-to-r from-purple-600 to-fuchsia-600 px-6 py-4 rounded-t-2xl">
                <h3 className="text-xl font-bold text-white">
                  {payEditingIndex >= 0 ? "تعديل مصروف" : "إضافة مصروف"}
                </h3>
              </div>

              <div className="p-6 space-y-4">
                {/* نوع المصروف + زر الإضافة السريع */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">نوع المصروف</label>
                  <div className="flex items-center gap-1">
                    <select
                      className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl
                              focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent
                              bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      value={payForm.grp_id}
                      onChange={(e) => setPayForm(f => ({ ...f, grp_id: e.target.value }))}
                    >
                      <option value="">— اختر —</option>
                      {paymentOptions.map(o => (
                        <option key={o.id} value={o.id}>{o.name}</option>
                      ))}
                    </select>

                    {/* زر + صغير ملاصق */}
                    <button
                      type="button"
                      title="إضافة نوع جديد"
                      onClick={openQuickType}
                      className="flex items-center justify-center w-9 h-9 border border-gray-300
                              dark:border-gray-600 rounded-xl text-fuchsia-600
                              hover:bg-fuchsia-50 dark:hover:bg-gray-600 transition"
                    >

                      +
                    </button>
                  </div>
                </div>

                {/* المبلغ */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">المبلغ</label>
                  <input
                    type="number" step="0.01"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl
                            focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent
                            bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="0.00"
                    value={payForm.amount}
                    onChange={(e) => setPayForm(f => ({ ...f, amount: e.target.value }))}
                  />
                </div>

                {/* التاريخ */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">التاريخ</label>
                  <input
                    type="date"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl
                            focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent
                            bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    value={payForm.expense_date || ""}
                    onChange={(e) => setPayForm(f => ({ ...f, expense_date: e.target.value }))}
                  />
                </div>

                {/* ملاحظات */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">ملاحظات</label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl
                            focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent
                            bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="اختياري"
                    value={payForm.notes || ""}
                    onChange={(e) => setPayForm(f => ({ ...f, notes: e.target.value }))}
                  />
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 rounded-b-2xl flex justify-end space-x-reverse space-x-3">
                <button className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600" onClick={closePayModal}>
                  إلغاء
                </button>
                <button className="px-6 py-2 bg-fuchsia-600 text-white rounded-xl hover:bg-fuchsia-700" onClick={savePaymentFromModal}>
                  {payEditingIndex >= 0 ? "تحديث" : "إضافة"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Traveler Expenses Modal (مصاريف عامة للمسافر) */}
        {showExpensesModal && expensesTravelerIndex >= 0 && travelers[expensesTravelerIndex] && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
              <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-6 py-4 rounded-t-2xl">
                <h3 className="text-xl font-bold text-white">
                  الصرفيات — {travelers[expensesTravelerIndex].name || 'المسافر'}
                </h3>
              </div>
              <div className="p-6 overflow-auto flex-1">
                <table className="app-table min-w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap">نوع المصروف</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap">التاريخ</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap">المبلغ</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap">ملاحظات</th>
                      <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap">إجراء</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                    {(travelers[expensesTravelerIndex].expenses || []).map((exp, expIdx) => (
                      <tr key={expIdx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">{exp.expense_type}</td>
                        <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">{exp.expense_date}</td>
                        <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtMoney(exp.amount)}</td>
                        <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400 max-w-[150px] truncate" title={exp.notes || ''}>{exp.notes || '—'}</td>
                        <td className="px-4 py-2 text-center whitespace-nowrap">
                          <button type="button" className="text-indigo-600 hover:underline mr-2 rtl:ml-2" onClick={() => editExpenseRow(expIdx)}>تعديل</button>
                          <button type="button" className="text-red-600 hover:underline" onClick={() => removeExpenseRow(expIdx)}>حذف</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl space-y-3">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{expenseEditingIndex >= 0 ? 'تعديل' : 'إضافة'}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">نوع المصروف</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                        value={expenseForm.expense_type}
                        onChange={e => setExpenseForm(f => ({ ...f, expense_type: e.target.value }))}
                      >
                        <option value="">— اختر —</option>
                        {paymentOptions.map(o => (
                          <option key={o.id} value={o.name}>{o.name}</option>
                        ))}
                        {/* عرض النوع الحالي إن وُجد ولم يعد في القائمة (مثلاً من تعديل سابق) */}
                        {expenseForm.expense_type && !paymentOptions.some(o => o.name === expenseForm.expense_type) && (
                          <option value={expenseForm.expense_type}>{expenseForm.expense_type}</option>
                        )}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">التاريخ</label>
                      <input
                        type="date"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                        value={expenseForm.expense_date}
                        onChange={e => setExpenseForm(f => ({ ...f, expense_date: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">المبلغ</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm text-right"
                        placeholder="0"
                        value={expenseForm.amount === "" || expenseForm.amount === null || expenseForm.amount === undefined ? "" : formatWithCommas(expenseForm.amount)}
                        onChange={e => {
                          const parsed = parseFormattedNumber(e.target.value);
                          setExpenseForm(f => ({ ...f, amount: parsed === "" ? "" : parsed }));
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">ملاحظات</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                        placeholder="اختياري"
                        value={expenseForm.notes}
                        onChange={e => setExpenseForm(f => ({ ...f, notes: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button type="button" className={`px-4 py-2 rounded-lg ${isSavingExpense ? 'bg-gray-400 cursor-not-allowed' : 'bg-amber-600 hover:bg-amber-700'} text-white text-sm font-medium`} onClick={saveExpenseFromModal} disabled={isSavingExpense}>
                      {expenseEditingIndex >= 0 ? 'تحديث' : 'إضافة'}
                    </button>
                    {expenseEditingIndex >= 0 && (
                      <button type="button" className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm" onClick={addExpenseRow}>
                        إلغاء
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 rounded-b-2xl flex justify-end">
                <button type="button" className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600" onClick={closeExpensesModal}>
                  إغلاق
                </button>
              </div>
            </div>
          </div>
        )}

        {showQuickTypeModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]">
            <div className="bg-white rounded-2xl w-full max-w-md">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-bold">إضافة نوع مصروف </h3>
              </div>
              <div className="p-6">
                <label className="block text-sm font-semibold mb-2">الاسم</label>
                <input
                  className="w-full px-4 py-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-fuchsia-500"
                  value={quickTypeName}
                  onChange={e => setQuickTypeName(e.target.value)}
                />
              </div>
              <div className="px-6 py-4 border-t flex justify-end gap-3">
                <button className="px-4 py-2 rounded-xl border" onClick={closeQuickType}>إلغاء</button>
                <button
                  className="px-4 py-2 rounded-xl bg-fuchsia-600 text-white hover:bg-fuchsia-700"
                  onClick={async () => {
                    try {
                      const nm = quickTypeName.trim();
                      if (!nm) return toast.error(t('agents.nameRequired'));

                      const resp = await api.post(`/v1/groups-payment`, { name: nm }, { headers: headersAuth });

                      toast.success(t('agents.added'));
                      const newId = resp?.data?.id ?? resp?.data?.data?.id ?? null;
                      await loadGroupPaymentOptions();
                      if (newId) setPayForm(f => ({ ...f, grp_id: newId }));
                      setShowQuickTypeModal(false); setShowPayModal(true);
                    } catch (e) {
                      const msg = e?.response?.data?.message || "";
                      // إذا مكرر: اختَر الموجود تلقائيًا
                      if (e?.response?.status === 422 && /exists/i.test(msg)) {
                        const nm = quickTypeName.trim().toLowerCase();
                        await loadGroupPaymentOptions();
                        const found = paymentOptions.find(o => o.name.trim().toLowerCase() === nm);
                        if (found) {
                          setPayForm(f => ({ ...f, grp_id: found.id }));
                          toast.success(t('agents.alreadyExistsSelected'));
                          setShowQuickTypeModal(false); setShowPayModal(true);
                          return;
                        }
                      }
                      console.error(e);
                      toast.error(msg || t('agents.addFailed'));
                    }
                  }}
                >
                  حفظ
                </button>
              </div>
            </div>
          </div>
        )}

        {showQuickAgentModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]">
            <div className="bg-white rounded-2xl w-full max-w-md">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-bold">إضافة وكيل</h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">{t('receiveCash.agentName')}</label>
                  <input
                    className="w-full px-4 py-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-fuchsia-500"
                    value={quickAgentName}
                    onChange={e => setQuickAgentName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">{t('travelers.primaryPhone')}</label>
                  <input
                    className="w-full px-4 py-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-fuchsia-500"
                    value={quickAgentPhone}
                    onChange={e => setQuickAgentPhone(e.target.value)}
                  />
                </div>
              </div>
              <div className="px-6 py-4 border-t flex justify-end gap-3">
                <button className="px-4 py-2 rounded-xl border" onClick={closeQuickAgent}>{t('common.cancel')}</button>
                <button
                  className="px-4 py-2 rounded-xl bg-fuchsia-600 text-white hover:bg-fuchsia-700"
                  onClick={async () => {
                    try {
                      const nm = quickAgentName.trim();
                      if (!nm) return toast.error(t('agents.nameRequired'));
                      const payload = { name: nm, phone: quickAgentPhone.trim(), branch_id: Number(group.branch_id) };
                      const resp = await api.post(`${API_BASE}/agents`, payload, { headers: headersAuth });
                      const newAgent = resp?.data?.agent ?? resp?.data?.data ?? resp?.data;
                      toast.success(t('agents.added'));
                      await loadAgents();
                      if (newAgent?.id) setTForm(f => ({ ...f, agent: newAgent.id }));
                      setShowQuickAgentModal(false);
                    } catch (e) {
                      const msg = e?.response?.data?.message || '';
                      if (e?.response?.status === 422 && /exists/i.test(msg)) {
                        const nm = quickAgentName.trim().toLowerCase();
                        try {
                          const listResp = await api.get(`${API_BASE}/agents`, {
                            params: { branch_id: group.branch_id, per_page: 100 },
                            headers: headersAuth,
                          });
                          const list = listResp?.data?.data || [];
                          const found = list.find(a => (a.name || '').trim().toLowerCase() === nm);
                          if (found) {
                            setTForm(f => ({ ...f, agent: found.id }));
                            toast.success(t('agents.alreadyExistsSelected'));
                            setShowQuickAgentModal(false);
                            return;
                          }
                        } catch (_e) {
                          // ignore
                        }
                      }
                      console.error(e);
                      toast.error(msg || t('agents.addFailed'));
                    }
                  }}
                >
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal تأكيد المسافر الموجود */}
        {showExistingTravelerModal && existingTravelerData && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[70]">
            <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md shadow-2xl">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {t('travelers.existingTravelerTitle') || 'مسافر موجود في النظام'}
                </h3>
              </div>
              <div className="p-6">
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {t('travelers.existingTravelerMessage') || 'هذا المسافر مسجل مسبقاً في النظام. هل تريد إضافته إلى هذه المجموعة؟'}
                </p>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400 text-sm">{t('travelers.name')}:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{existingTravelerData.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400 text-sm">{t('travelers.primaryPhone')}:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{existingTravelerData.phone1 || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400 text-sm">{t('travelers.passportNumber')}:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{existingTravelerData.passport || '-'}</span>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                <button
                  className="px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  onClick={cancelUseExistingTraveler}
                >
                  {t('common.cancel') || 'إلغاء'}
                </button>
                <button
                  className="px-4 py-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 transition-colors font-medium"
                  onClick={confirmUseExistingTraveler}
                >
                  {t('travelers.useExisting') || 'نعم، أضفه'}
                </button>
              </div>
            </div>
          </div>
        )}


        {/* Submit Button */}
        <div className="mt-8 flex justify-center">
          <button
            className={`px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-200 transform hover:scale-105 flex items-center space-x-reverse space-x-3 shadow-lg ${isSubmitting
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
              }`}
            onClick={submitGroup}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin w-5 h-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>{t('common.loading')}</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>{t('groups.saveGroup')}</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Traveler Modal - Green Theme */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 rounded-t-2xl">
              <h3 className="text-xl font-bold text-white">
                {editingIndex >= 0 ? t('travelers.editTraveler') : t('travelers.addTraveler')}
              </h3>
            </div>

            <div className="p-6 space-y-6">
              {/* الحقول الأساسية */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">{t('travelers.fullName')}</label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-300"
                    placeholder={t('travelers.fullNamePlaceholder')}
                    value={tForm.name}
                    onChange={(e) => setTForm((f) => ({ ...f, name: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">{t('travelers.primaryPhone')}</label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-300"
                    placeholder={t('travelers.primaryPhonePlaceholder')}
                    value={tForm.phone1}
                    onChange={(e) => setTForm((f) => ({ ...f, phone1: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">{t('travelers.secondaryPhone')}</label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-300"
                    placeholder={t('travelers.secondaryPhonePlaceholder')}
                    value={tForm.phone2}
                    onChange={(e) => setTForm((f) => ({ ...f, phone2: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">{t('travelers.passportNumber')}</label>
                  <div className="relative">
                    <input
                      type="text"
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-300"
                      placeholder={t('travelers.passportNumberPlaceholder')}
                      value={tForm.passport}
                      onChange={(e) => setTForm((f) => ({ ...f, passport: e.target.value }))}
                      onBlur={handlePassportBlur}
                    />
                    {passportCheckLoading && (
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        <svg className="animate-spin h-5 w-5 text-emerald-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">{t('travelers.addedDate')}</label>
                  <input
                    type="date"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    value={tForm.added_date}
                    onChange={(e) => setTForm((f) => ({ ...f, added_date: e.target.value }))}
                  />
                </div>
              </div>

              {/* نوع الدفع والنقد المستلم - في الأعلى */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 text-center">{t('travelers.paymentTypeLabel')}</label>
                  <select
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-center bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    value={tForm.paytype}
                    onChange={(e) => setTForm((f) => ({ ...f, paytype: e.target.value }))}
                  >
                    <option value="cash">{t('travelers.cash')}</option>
                    <option value="installments">{t('travelers.installments')}</option>
                    <option value="deferred">{t('travelers.deferred')}</option>
                    <option value="free">{t('travelers.free')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">{t('travelers.cashReceived')}</label>
                  <input
                    type="number"
                    step="0.01"
                    className={`w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-300 ${(tForm.paytype === 'free' || tForm.paytype === 'deferred') ? 'opacity-50 cursor-not-allowed' : ''}`}
                    placeholder="0.00"
                    value={(tForm.paytype === 'free' || tForm.paytype === 'deferred') ? 0 : tForm.cash}
                    onChange={(e) => setTForm((f) => ({ ...f, cash: e.target.value }))}
                    disabled={tForm.paytype === 'free' || tForm.paytype === 'deferred'}
                  />
                </div>
              </div>

              {/* اسم الوكيل وعمولة الوكيل - متاح لجميع أنواع الدفع (نقدي، أقساط، مجاني) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 text-center">{t('travelers.agentName')}</label>
                  <div className="flex space-x-reverse space-x-2">
                    <select
                      className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-center bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      value={tForm.agent}
                      onChange={(e) => setTForm((f) => ({ ...f, agent: e.target.value }))}
                    >
                      <option value="">{t('travelers.selectAgent')}</option>
                      {agents.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name} {a.phone ? `(${a.phone})` : ""}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="px-4 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors duration-150"
                      onClick={quickCreateAgent}
                    >
                      +
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">{t('travelers.agentCommission')}</label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-300"
                    placeholder={t('travelers.agentCommissionPlaceholder')}
                    value={tForm.agent_money}
                    onChange={(e) => setTForm((f) => ({ ...f, agent_money: e.target.value }))}
                  />
                </div>
              </div>

              {/* إجمالي السعر، مدة التسديد، القسط الشهري */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">{t('travelers.totalPrice')}</label>
                  <input
                    type="number"
                    step="0.01"
                    className={`w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-300 ${tForm.paytype === 'free' ? 'opacity-50 cursor-not-allowed' : ''}`}
                    placeholder={t('travelers.totalAmountPlaceholder')}
                    value={tForm.paytype === 'free' ? 0 : tForm.total_price}
                    onChange={(e) => setTForm((f) => ({ ...f, total_price: e.target.value }))}
                    disabled={tForm.paytype === 'free'}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">{t('travelers.paymentDuration')}</label>
                  <input
                    type="number"
                    min="1"
                    className={`w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-300 ${(tForm.paytype === 'cash' || tForm.paytype === 'free' || tForm.paytype === 'deferred') ? 'opacity-50 cursor-not-allowed' : ''}`}
                    placeholder={t('travelers.paymentDurationPlaceholder')}
                    value={tForm.gr_interval}
                    onChange={(e) => setTForm((f) => ({ ...f, gr_interval: e.target.value }))}
                    disabled={tForm.paytype === 'cash' || tForm.paytype === 'free' || tForm.paytype === 'deferred'}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">{t('travelers.monthlyInstallment')}</label>
                  <input
                    type="number"
                    step="0.01"
                    className={`w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-300 ${(tForm.paytype === 'cash' || tForm.paytype === 'free' || tForm.paytype === 'deferred') ? 'opacity-50 cursor-not-allowed' : ''}`}
                    placeholder={t('travelers.monthlyInstallmentPlaceholder')}
                    value={tForm.price}
                    onChange={(e) => setTForm((f) => ({ ...f, price: e.target.value }))}
                    disabled={tForm.paytype === 'cash' || tForm.paytype === 'free' || tForm.paytype === 'deferred'}
                  />
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 rounded-b-2xl flex justify-end space-x-reverse space-x-3">
              <button
                className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-150"
                onClick={closeModal}
              >
                {t('common.cancel')}
              </button>
              <button
                className="px-6 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors duration-150"
                onClick={saveTravelerFromModal}
              >
                {editingIndex >= 0 ? t('common.update') : t('common.add')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
